import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { UploadArea } from '../ui/UploadArea';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { TemplateSlot } from '../../hooks/useTemplates';
import { Asset } from '../../hooks/useAssets';
import { CROP_ANCHOR_OPTIONS, getAcceptedTypes } from '../../lib/slotProps';
import { getVisibleCharCount } from '../../lib/textUtils';
import { CheckCircle2, Circle } from 'lucide-react';
import { getDreamCatEffectPath, storageAssetUrl } from '../../lib/dreamCatEffects';
import { getOpeningVideoCount, isOpeningVideoSlot } from '../../lib/openingVideoSlots';
import { SlotMediaType, validateAssetTypeForSlot } from '../../lib/assetTypeValidation';

/** Tolerance for treating two aspect ratios as equal (avoids a near-zero crop box). */
const ASPECT_RATIO_TOLERANCE = 0.01;

/** Parse a "W:H" string into a numeric ratio (W/H). Returns null if invalid. */
function parseRatio(s: string): number | null {
  const parts = s.split(':');
  if (parts.length !== 2) return null;
  const a = parseFloat(parts[0]);
  const b = parseFloat(parts[1]);
  if (!isFinite(a) || !isFinite(b) || b === 0 || a <= 0) return null;
  return a / b;
}

/**
 * Computes the crop box in PIXEL coordinates for cover-fit mode.
 *
 * Algorithm (matches the implementation spec exactly):
 *   - if source is wider than target:  cropW = displayedH * targetRatio,  cropH = displayedH
 *   - if source is taller than target: cropW = displayedW, cropH = displayedW / targetRatio
 *
 * @param displayedW  - CSS-rendered width of the source media element (px)
 * @param displayedH  - CSS-rendered height of the source media element (px)
 * @param targetRatio - target slot aspect ratio as a number (width / height), e.g. 9/16 = 0.5625
 * @param cropAnchor  - operator-selected anchor, e.g. 'center', 'top', 'bottom-left'
 * @returns Pixel-coordinate crop rectangle, or null if no crop is needed.
 */
function computeCropBox(
  displayedW: number,
  displayedH: number,
  targetRatio: number,
  cropAnchor: string,
): { x: number; y: number; w: number; h: number } | null {
  if (displayedW <= 0 || displayedH <= 0 || targetRatio <= 0) return null;

  const srcRatio = displayedW / displayedH;

  if (Math.abs(srcRatio - targetRatio) < ASPECT_RATIO_TOLERANCE) return null; // effectively no crop

  let boxW: number, boxH: number, x: number, y: number;

  if (srcRatio > targetRatio) {
    // Source wider than target → crop horizontally, full source height visible
    boxH = displayedH;
    boxW = displayedH * targetRatio;
    y = 0;
    if (cropAnchor.includes('left')) x = 0;
    else if (cropAnchor.includes('right')) x = displayedW - boxW;
    else x = (displayedW - boxW) / 2;
  } else {
    // Source taller than target → crop vertically, full source width visible
    boxW = displayedW;
    boxH = displayedW / targetRatio;
    x = 0;
    if (cropAnchor.includes('top')) y = 0;
    else if (cropAnchor.includes('bottom')) y = displayedH - boxH;
    else y = (displayedH - boxH) / 2;
  }

  return { x, y, w: boxW, h: boxH };
}

/**
 * Renders the crop-region overlay using absolute PIXEL coordinates.
 * All dimensions (x, y, w, h) are in CSS pixels relative to the media element.
 */
const CropOverlay: React.FC<{
  cropBox: { x: number; y: number; w: number; h: number };
  displayedW: number;
  displayedH: number;
}> = ({ cropBox, displayedW, displayedH }) => (
  <>
    {cropBox.y >= 1 && (
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: `${cropBox.y}px`,
        background: 'rgba(0,0,0,0.55)',
        pointerEvents: 'none',
      }} />
    )}
    {cropBox.y + cropBox.h <= displayedH - 1 && (
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        height: `${displayedH - cropBox.y - cropBox.h}px`,
        background: 'rgba(0,0,0,0.55)',
        pointerEvents: 'none',
      }} />
    )}
    {cropBox.x >= 1 && (
      <div style={{
        position: 'absolute',
        top: `${cropBox.y}px`,
        left: 0,
        width: `${cropBox.x}px`,
        height: `${cropBox.h}px`,
        background: 'rgba(0,0,0,0.55)',
        pointerEvents: 'none',
      }} />
    )}
    {cropBox.x + cropBox.w <= displayedW - 1 && (
      <div style={{
        position: 'absolute',
        top: `${cropBox.y}px`,
        right: 0,
        width: `${displayedW - cropBox.x - cropBox.w}px`,
        height: `${cropBox.h}px`,
        background: 'rgba(0,0,0,0.55)',
        pointerEvents: 'none',
      }} />
    )}
    <div style={{
      position: 'absolute',
      top: `${cropBox.y}px`,
      left: `${cropBox.x}px`,
      width: `${cropBox.w}px`,
      height: `${cropBox.h}px`,
      border: '2px solid rgba(255,255,255,0.9)',
      boxSizing: 'border-box',
      pointerEvents: 'none',
    }} />
  </>
);

/**
 * Single live preview for an image slot — full original image with a crop-region overlay.
 * For 6:19 slots the crop frame is suppressed and the original image is shown as-is.
 */
export const ImageSlotPreview: React.FC<{
  file?: File | null;
  /** Direct URL to use when no File is available (e.g. gallery-dropped asset). */
  url?: string | null;
  fitMode: string | null | undefined;
  cropAnchor: string | null | undefined;
  slotAspectRatio: string | null | undefined;
}> = ({ file, url: directUrl, fitMode, cropAnchor, slotAspectRatio }) => {
  const [objectUrl, setObjectUrl] = useState<string>('');
  const [displayedSize, setDisplayedSize] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    if (!file) return;
    const created = URL.createObjectURL(file);
    setObjectUrl(created);
    setDisplayedSize(null);
    return () => URL.revokeObjectURL(created);
  }, [file]);

  const url = objectUrl || directUrl || '';

  // 6:19 slots must show the original image with no crop frame at all.
  const is619 = slotAspectRatio === '6:19';

  // Parse the aspect ratio string to a numeric ratio once.
  const targetRatio = useMemo(() => {
    if (!slotAspectRatio) return null;
    return parseRatio(slotAspectRatio);
  }, [slotAspectRatio]);

  const cropBox = useMemo(() => {
    if (is619 || fitMode !== 'cover' || !targetRatio || !displayedSize) return null;
    return computeCropBox(
      displayedSize.w,
      displayedSize.h,
      targetRatio,
      cropAnchor ?? 'center',
    );
  }, [is619, fitMode, targetRatio, displayedSize, cropAnchor]);

  if (!url) return null;

  const overlayLabel = is619
    ? 'Preview — full image visible (no crop)'
    : cropBox
    ? 'Preview — highlighted area will be visible'
    : fitMode === 'contain'
    ? 'Preview — full image visible (no crop)'
    : 'Preview';

  return (
    <div>
      <p className="text-xs text-text-secondary mb-1">{overlayLabel}</p>
      <div className="relative rounded-md overflow-hidden bg-bg-secondary border border-border-subtle">
        <div className="relative" style={{ lineHeight: 0 }}>
          <img
            src={url}
            alt="Preview"
            onLoad={(e) => {
              const img = e.currentTarget;
              // Use CSS-rendered dimensions so the crop box pixel values match the overlay.
              setDisplayedSize({ w: img.clientWidth, h: img.clientHeight });
            }}
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
          {cropBox && displayedSize && (
            <CropOverlay cropBox={cropBox} displayedW={displayedSize.w} displayedH={displayedSize.h} />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * First-frame preview for a video slot with a crop-region overlay.
 * Always uses cover-fit logic; the slot aspect ratio drives the crop frame shape.
 */
export const VideoSlotPreview: React.FC<{
  file?: File | null;
  /** Direct URL to use when no File is available (e.g. gallery-dropped asset). */
  url?: string | null;
  cropAnchor: string | null | undefined;
  slotAspectRatio: string | null | undefined;
  trimStartSeconds?: number;
  trimDurationSeconds?: number | null;
  loopSelectedSegment?: boolean;
  /**
   * When true the video plays automatically and loops instead of freezing on
   * the first frame. Use this for pre-rendered assets (e.g. endcard videos)
   * where the full motion preview is more useful than a crop overlay.
   */
  autoPlay?: boolean;
  loop?: boolean;
  /** Current template id — used to gate template-specific overlay logic. */
  templateId?: string | null;
  /** Current slot id — used to gate template-specific overlay logic. */
  slotId?: string | null;
  /**
   * Active market code (e.g. 'kr', 'id', 'en', 'jp', 'in', 'global').
   * Used to select the correct Dream Cat drop-card overlay for the last active
   * opening video slot preview.  Falls back to EN for unknown/missing markets.
   */
  market?: string | null;
  /** Explicit Effect Icon variant override. "auto" or null/undefined defers to market. */
  dropCardVariant?: string | null;
  /** Direct URL override for Effect Icon overlay. */
  dropCardAssetUrl?: string | null;
  /** Number of active opening video slots (1–5). Determines which slot gets the effect icon overlay. */
  openingVideoCount?: number;
}> = ({
  file,
  url: directUrl,
  cropAnchor,
  slotAspectRatio,
  trimStartSeconds = 0,
  trimDurationSeconds = null,
  loopSelectedSegment = false,
  autoPlay = false,
  loop = false,
  templateId,
  slotId,
  market,
  dropCardVariant,
  dropCardAssetUrl,
  openingVideoCount,
}) => {
  const [objectUrl, setObjectUrl] = useState<string>('');
  const [displayedSize, setDisplayedSize] = useState<{ w: number; h: number } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!file) return;
    const created = URL.createObjectURL(file);
    setObjectUrl(created);
    setDisplayedSize(null);
    return () => URL.revokeObjectURL(created);
  }, [file]);

  const url = objectUrl || directUrl || '';

  // Parse the aspect ratio string to a numeric ratio once.
  const targetRatio = useMemo(() => {
    if (!slotAspectRatio) return null;
    return parseRatio(slotAspectRatio);
  }, [slotAspectRatio]);

  const cropBox = useMemo(() => {
    if (!targetRatio || !displayedSize) return null;
    return computeCropBox(
      displayedSize.w,
      displayedSize.h,
      targetRatio,
      cropAnchor ?? 'center',
    );
  }, [targetRatio, displayedSize, cropAnchor]);

  const hasValidTrimWindow =
    loopSelectedSegment &&
    trimDurationSeconds !== null &&
    trimDurationSeconds > 0;
  const normalizedTrimStart = Math.max(0, trimStartSeconds);
  const trimEnd = hasValidTrimWindow
    ? normalizedTrimStart + trimDurationSeconds
    : null;

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !hasValidTrimWindow) return;
    if (!Number.isFinite(v.duration) || v.duration <= 0) return;
    const maxStart = Math.max(0, v.duration - trimDurationSeconds);
    const clampedStart = Math.min(normalizedTrimStart, maxStart);
    v.currentTime = clampedStart;
    const maybePlay = v.play();
    if (maybePlay && typeof maybePlay.catch === 'function') {
      maybePlay.catch(() => undefined);
    }
  }, [hasValidTrimWindow, normalizedTrimStart, trimDurationSeconds, url]);

  // Determine whether to show the ai-dream-cat drop-card overlay on the last active opening video slot preview.
  const showDropCardOverlay =
    templateId === 'ai-dream-cat-v1' &&
    !!slotId &&
    isOpeningVideoSlot(slotId) &&
    slotId === `video${getOpeningVideoCount(openingVideoCount)}`;
  const selectedDropCardUrl = showDropCardOverlay
    ? (dropCardAssetUrl || storageAssetUrl(getDreamCatEffectPath(market, dropCardVariant)))
    : null;

  if (!url) return null;

  return (
    <div>
      <p className="text-xs text-text-secondary mb-1">
        {hasValidTrimWindow
          ? 'Preview — looping selected source segment'
          : cropBox
          ? 'Preview — highlighted area will be visible'
          : 'Preview'}
      </p>
      <div className="relative rounded-md overflow-hidden bg-bg-secondary border border-border-subtle">
        <div className="relative" style={{ lineHeight: 0 }}>
          <video
            ref={videoRef}
            src={url}
            muted
            preload="metadata"
            autoPlay={autoPlay || hasValidTrimWindow}
            loop={hasValidTrimWindow ? false : loop}
            onLoadedMetadata={(e) => {
              const v = e.currentTarget;
              if (hasValidTrimWindow && trimDurationSeconds !== null) {
                const maxStart = Math.max(0, v.duration - trimDurationSeconds);
                const clampedStart = Math.min(normalizedTrimStart, maxStart);
                v.currentTime = clampedStart;
                const maybePlay = v.play();
                if (maybePlay && typeof maybePlay.catch === 'function') {
                  maybePlay.catch(() => undefined);
                }
              } else if (!autoPlay) {
                // Seek to just past the start so the first frame is painted.
                v.currentTime = 0.001;
              }
            }}
            onTimeUpdate={(e) => {
              if (!hasValidTrimWindow || trimEnd === null) return;
              const v = e.currentTarget;
              if (v.currentTime >= trimEnd || v.currentTime < normalizedTrimStart) {
                v.currentTime = normalizedTrimStart;
                const maybePlay = v.play();
                if (maybePlay && typeof maybePlay.catch === 'function') {
                  maybePlay.catch(() => undefined);
                }
              }
            }}
            onSeeked={(e) => {
              const v = e.currentTarget;
              // Read CSS-rendered dimensions after the first frame is painted.
              setDisplayedSize({ w: v.clientWidth, h: v.clientHeight });
            }}
            onLoadedData={(e) => {
              // For autoplay videos, capture dimensions once data is ready.
              if (autoPlay) {
                const v = e.currentTarget;
                setDisplayedSize({ w: v.clientWidth, h: v.clientHeight });
              }
            }}
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
          {cropBox && displayedSize && (
            <CropOverlay cropBox={cropBox} displayedW={displayedSize.w} displayedH={displayedSize.h} />
          )}
          {/* AI Dream Cat — transparent drop-card overlay for the last active opening video slot */}
          {selectedDropCardUrl && (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              key={selectedDropCardUrl}
              src={selectedDropCardUrl}
              autoPlay
              muted
              loop
              playsInline
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export interface SlotAsset {
  slotId: string;
  file: File | null;
  textValue: string | null;
  /** Operator-selected crop anchor for cover-fit image/video slots. */
  cropAnchor: string;
  /** Selected source start time for trim-enabled video slots. */
  trimStartSeconds?: number;
  /** Duration detected from loaded video metadata, in seconds. */
  videoDurationSeconds?: number;
  /**
   * Storage URL of an asset used for UI preview (e.g. a proxy MP4 for endcard).
   * For regular gallery assets this is the same URL used for rendering.
   * When set, the asset does not need to be re-uploaded during job creation.
   */
  storageUrl?: string;
  /**
   * Render-specific URL that overrides `storageUrl` when building Remotion input props.
   * Used for endcard assets where the preview (proxy MP4) differs from the render
   * source (original MOV). When absent, `storageUrl` is used for rendering as well.
   */
  renderUrl?: string;
  /** Display name for a gallery-dropped asset (used when `file` is null). */
  assetName?: string;
  /** Display size (bytes) of a gallery-dropped asset (used when `file` is null). */
  assetSize?: number;
}

export interface AssetSlotMapperProps {
  slots: TemplateSlot[];
  slotAssets: SlotAsset[];
  onAssetAdded: (slotId: string, file: File) => void;
  onAssetRemoved: (slotId: string) => void;
  onTextChanged: (slotId: string, value: string) => void;
  onCropAnchorChanged: (slotId: string, anchor: string) => void;
  /**
   * Called when an asset is dropped from the gallery. The asset is already in
   * Firebase Storage so no re-upload is needed — the `storageUrl` can be used directly.
   * `renderUrl` is an optional override for the Remotion render URI (e.g. the original
   * MOV for endcard assets when `storageUrl` points to a lower-res proxy MP4).
   */
  onGalleryAssetAdded?: (slotId: string, storageUrl: string, name: string, size: number, renderUrl?: string) => void;
  disabled?: boolean;
  /** The currently selected image slot id (for the shared preview panel). */
  selectedSlotId?: string | null;
  /** Called when an image slot card is clicked/focused. */
  onSlotSelected?: (slotId: string) => void;
}

export const AssetSlotMapper: React.FC<AssetSlotMapperProps> = ({
  slots,
  slotAssets,
  onAssetAdded,
  onAssetRemoved,
  onTextChanged,
  onCropAnchorChanged,
  onGalleryAssetAdded,
  disabled = false,
  selectedSlotId,
  onSlotSelected,
}) => {
  const [dragOverSlotId, setDragOverSlotId] = useState<string | null>(null);
  const [slotWarnings, setSlotWarnings] = useState<Record<string, string>>({});

  const getSlotAsset = (slotId: string): SlotAsset | undefined => {
    return slotAssets.find((sa) => sa.slotId === slotId);
  };


  const isSlotComplete = (slot: TemplateSlot, slotAsset?: SlotAsset): boolean => {
    if (!slotAsset) return false;
    if (slot.type === 'text') {
      return !!slotAsset.textValue && slotAsset.textValue.trim().length > 0;
    }
    return !!slotAsset.file || !!slotAsset.storageUrl;
  };

  const completedCount = slots
    .filter((slot) => slot.visible !== false)
    .filter((slot) =>
      isSlotComplete(slot, getSlotAsset(slot.slotId))
    ).length;

  // Handle drop from asset gallery or native OS file drag
  const handleDrop = (e: React.DragEvent, slotId: string, slotType: 'image' | 'video' | 'audio') => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSlotId(null);

    try {
      // Try to get asset data from gallery
      const assetData = e.dataTransfer.getData('application/json');
      if (assetData) {
        // Internal gallery drag: asset is already in Firebase Storage.
        const asset: Asset = JSON.parse(assetData);

        const validation = validateAssetTypeForSlot(slotType as SlotMediaType, {
          declaredType: asset.type,
          mimeType: asset.mimeType,
          filename: asset.name,
        });
        if (!validation.valid) {
          setSlotWarnings((prev) => ({ ...prev, [slotId]: validation.message ?? 'Invalid asset type for this slot.' }));
          return;
        }
        setSlotWarnings((prev) => {
          const next = { ...prev };
          delete next[slotId];
          return next;
        });

        // The asset is already in Firebase Storage — pass the URL directly to avoid
        // a cross-origin fetch (which fails in production due to CORS restrictions).
        if (onGalleryAssetAdded) {
          onGalleryAssetAdded(slotId, asset.storageUrl, asset.name, asset.size);
        }
      } else if (e.dataTransfer.files.length > 0) {
        // Native OS file drag: upload through the existing slot upload flow.
        const file = e.dataTransfer.files[0];
        const validation = validateAssetTypeForSlot(slotType as SlotMediaType, {
          mimeType: file.type,
          filename: file.name,
        });
        if (!validation.valid) {
          setSlotWarnings((prev) => ({ ...prev, [slotId]: validation.message ?? 'Invalid asset type for this slot.' }));
          return;
        }
        setSlotWarnings((prev) => {
          const next = { ...prev };
          delete next[slotId];
          return next;
        });
        onAssetAdded(slotId, file);
      }
    } catch (error) {
      console.error('Failed to handle dropped asset:', error);
    }
  };

  const handleDragOver = (e: React.DragEvent, slotId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSlotId(slotId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSlotId(null);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Upload Assets & Content</CardTitle>
          <Badge variant="info">
            {completedCount} / {slots.length}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {slots.filter((slot) => slot.visible !== false).map((slot) => {
            const slotAsset = getSlotAsset(slot.slotId);
            const isComplete = isSlotComplete(slot, slotAsset);

            const isMediaSlot = slot.type === 'image' || slot.type === 'video';

            return (
              <div
                key={slot.slotId}
                className={`
                  p-4 bg-bg-tertiary border rounded-md transition-all duration-150
                  ${isMediaSlot ? 'cursor-pointer' : ''}
                  ${isMediaSlot && selectedSlotId === slot.slotId
                    ? 'border-accent-red ring-1 ring-accent-red'
                    : 'border-border-subtle'}
                `}
                onClick={isMediaSlot && onSlotSelected ? () => onSlotSelected(slot.slotId) : undefined}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {isComplete ? (
                        <CheckCircle2 className="w-4 h-4 text-status-online" />
                      ) : (
                        <Circle className="w-4 h-4 text-status-offline" />
                      )}
                      <h4 className="text-sm font-semibold text-text-primary">
                        {slot.label}
                      </h4>
                      {slot.required && (
                        <Badge size="sm" variant="error">
                          Required
                        </Badge>
                      )}
                    </div>
                    {slot.description && (
                      <p className="text-sm text-text-secondary ml-6 leading-relaxed">
                        {slot.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {slot.specs?.aspect_ratio && (
                      <Badge size="sm" variant="default">
                        {slot.specs.aspect_ratio}
                      </Badge>
                    )}
                    <Badge size="sm" variant="default">
                      {slot.type}
                    </Badge>
                  </div>
                </div>

                {/* Text Input for text slots */}
                {slot.type === 'text' && (() => {
                  const textValue = slotAsset?.textValue ?? '';
                  const maxChars = slot.specs?.max_chars ?? null;
                  const visibleCharCount = getVisibleCharCount(textValue, slot.slotId);
                  const isOverLimit = maxChars !== null && visibleCharCount > maxChars;
                  return (
                    <>
                      <Input
                        type="text"
                        placeholder={`Enter ${slot.label.toLowerCase()}`}
                        value={textValue}
                        onChange={(e) => onTextChanged(slot.slotId, e.target.value)}
                        disabled={disabled}
                      />
                      {maxChars !== null && (
                        <p className={`text-xs mt-1 text-right ${isOverLimit ? 'text-accent-red font-medium' : 'text-text-tertiary'}`}>
                          {visibleCharCount} / {maxChars}
                          {isOverLimit && ' — exceeds limit'}
                        </p>
                      )}
                    </>
                  );
                })()}

                {/* File Upload for image/video/audio slots */}
                {(slot.type === 'image' || slot.type === 'video' || slot.type === 'audio') && (
                  <div
                    onDrop={(e) => handleDrop(e, slot.slotId, slot.type as 'image' | 'video' | 'audio')}
                    onDragOver={(e) => handleDragOver(e, slot.slotId)}
                    onDragLeave={handleDragLeave}
                    className={`
                      transition-all duration-200 rounded-lg
                      ${dragOverSlotId === slot.slotId ? 'ring-2 ring-accent-red ring-offset-2' : ''}
                    `}
                  >
                    <UploadArea
                      onFilesAdded={(files) => {
                        const file = files[0];
                        if (!file) return;
                        const validation = validateAssetTypeForSlot(slot.type as SlotMediaType, {
                          mimeType: file.type,
                          filename: file.name,
                        });
                        if (!validation.valid) {
                          setSlotWarnings((prev) => ({ ...prev, [slot.slotId]: validation.message ?? 'Invalid asset type for this slot.' }));
                          return;
                        }
                        setSlotWarnings((prev) => {
                          const next = { ...prev };
                          delete next[slot.slotId];
                          return next;
                        });
                        onAssetAdded(slot.slotId, file);
                      }}
                      onFilesRejected={(rejections) => {
                        const file = rejections[0]?.file;
                        const validation = validateAssetTypeForSlot(slot.type as SlotMediaType, {
                          mimeType: file?.type,
                          filename: file?.name,
                        });
                        setSlotWarnings((prev) => ({ ...prev, [slot.slotId]: validation.message ?? 'Invalid asset type for this slot.' }));
                      }}
                      onFileRemove={() => onAssetRemoved(slot.slotId)}
                      uploadedFiles={
                        slotAsset?.file
                          ? [{ file: slotAsset.file }]
                          : slotAsset?.storageUrl
                          ? [{ name: slotAsset.assetName ?? 'Gallery asset', size: slotAsset.assetSize }]
                          : []
                      }
                      accept={getAcceptedTypes(slot.type as 'image' | 'video' | 'audio')}
                      maxFiles={1}
                      disabled={disabled}
                      helperText={`Upload ${slot.type} or drag from gallery (max 100MB)`}
                    />
                    {slotWarnings[slot.slotId] && (
                      <p className="mt-2 text-xs text-status-warning">{slotWarnings[slot.slotId]}</p>
                    )}
                  </div>
                )}

                {/* Crop anchor selector — shown for cover-fit image/video slots */}
                {(slot.type === 'image' || slot.type === 'video') &&
                  slot.specs?.fit_mode !== 'contain' &&
                  slot.specs?.show_crop_anchor !== false && (
                    <div className="mt-3 flex items-center gap-3">
                      <label className="text-xs text-text-secondary whitespace-nowrap">
                        Crop anchor:
                      </label>
                      <Select
                        value={slotAsset?.cropAnchor ?? slot.specs?.crop_anchor ?? 'center'}
                        onChange={(value) => onCropAnchorChanged(slot.slotId, value)}
                        options={CROP_ANCHOR_OPTIONS as unknown as { value: string; label: string }[]}
                        disabled={disabled}
                        className="flex-1 text-xs"
                      />
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
